import React, {useState, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {KTSVG} from '../../_metronic/helpers'
import useAuth from '../../hooks/useAuth'
import useChatbotRedux from '../../hooks/useChatbotRedux'
import './FloatingChatbot.css'

interface ChatMessage {
  id: string
  text: string
  isUser: boolean
  timestamp: Date
}

interface ChatbotResponse {
  _id: string
  question: string
  answer: string
  keywords: string[]
  category: 'pre-login' | 'post-login'
  language: 'en' | 'fr' | 'ar'
  isActive: boolean
  priority: number
  userRoles?: string[]
}

export default function FloatingChatbot() {
  const intl = useIntl()
  const {me} = useAuth()
  const {fetchActiveResponses, searchResponses} = useChatbotRedux()
  
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputText, setInputText] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [responses, setResponses] = useState<ChatbotResponse[]>([])
  const [showSuggestions, setShowSuggestions] = useState(true)
  const [hasNewMessage, setHasNewMessage] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [suggestedKeywords, setSuggestedKeywords] = useState<string[]>([])
  const [isPreLogin, setIsPreLogin] = useState(!me)
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)

  // Function to get translated questions
  const getTranslatedQuestions = () => {
    return [
      intl.formatMessage({ id: 'CHATBOT.QUESTIONS.MAIN_PARTNERS' }),
      intl.formatMessage({ id: 'CHATBOT.QUESTIONS.WHAT_IS_MAAREFATI' }),
      intl.formatMessage({ id: 'CHATBOT.QUESTIONS.HOW_TO_GET_STARTED' }),
      intl.formatMessage({ id: 'CHATBOT.QUESTIONS.WHAT_SERVICES_OFFER' }),
      intl.formatMessage({ id: 'CHATBOT.QUESTIONS.HOW_CREATE_ACCOUNT' }),
      intl.formatMessage({ id: 'CHATBOT.QUESTIONS.HOW_CONTACT_SUPPORT' }),
      intl.formatMessage({ id: 'CHATBOT.QUESTIONS.WHAT_LANGUAGES_SUPPORTED' }),
      intl.formatMessage({ id: 'CHATBOT.QUESTIONS.WHO_CAN_USE_MAAREFATI' }),
      intl.formatMessage({ id: 'CHATBOT.QUESTIONS.MOBILE_ACCESS' }),
      intl.formatMessage({ id: 'CHATBOT.QUESTIONS.WHY_CHOOSE_MAAREFATI' }),
      intl.formatMessage({ id: 'CHATBOT.QUESTIONS.HOW_JOIN_FAMILY' }),
      intl.formatMessage({ id: 'CHATBOT.QUESTIONS.HOW_REGISTER' }),
      intl.formatMessage({ id: 'CHATBOT.QUESTIONS.HOW_DOWNLOAD_APP' })
    ]
  }
  
  // Set fallback keywords immediately to ensure they're always available
    useEffect(() => {
      const fallbackKeywords = me
        ? ["Help with my account", "Technical support", "Feature request", "Report a bug"]
        : getTranslatedQuestions()
      console.log('Setting initial fallback keywords:', fallbackKeywords)
      setSuggestedKeywords(fallbackKeywords)
    }, [me, intl.locale])

  // Load active responses on component mount and when user changes
  useEffect(() => {
    loadActiveResponses()
    loadSuggestedKeywords()
    setIsPreLogin(!me)
    // Ensure suggestions are always visible
    setShowSuggestions(true)
  }, [me, intl.locale])

  const loadSuggestedKeywords = async () => {
    setLoadingSuggestions(true)
    try {
      console.log('=== LOADING SUGGESTED KEYWORDS ===')
      console.log('User authenticated:', !!me)
      console.log('User role:', me?.role)
      console.log('Language:', intl.locale)
      console.log('API URL:', process.env.REACT_APP_API_URL)
      
      // Get suggested keywords from the chatbot responses
      const category = me ? 'post-login' : 'pre-login'
      console.log('Fetching for category:', category)
      
      console.log('Calling fetchActiveResponses with params:', {
        category,
        language: intl.locale as 'en' | 'fr' | 'ar',
        userRole: me?.role
      })
      
      const result = await fetchActiveResponses({
        category,
        language: intl.locale as 'en' | 'fr' | 'ar',
        userRole: me?.role
      })
      
      console.log('API Result:', result)
      console.log('API Result type:', typeof result)
      console.log('API Result payload:', result.payload)
      console.log('API Result payload type:', typeof result.payload)
      
      let keywords: string[] = []
      
      // Handle different response structures
      let responses: ChatbotResponse[] = []
      
      if (result.payload && typeof result.payload === 'object') {
        if ('data' in result.payload && result.payload.data) {
          const payload = result.payload as { data: { responses: ChatbotResponse[] } }
          responses = payload.data.responses || []
          console.log('Responses from payload.data.responses:', responses)
        } else if ('responses' in result.payload) {
          responses = (result.payload as { responses: ChatbotResponse[] }).responses || []
          console.log('Responses from payload.responses:', responses)
        } else if (Array.isArray(result.payload)) {
          responses = result.payload as ChatbotResponse[]
          console.log('Responses from direct array:', responses)
        }
      }
      
      console.log('Final responses array:', responses)
      console.log('Responses count:', responses.length)
      
      // Extract suggested keywords from responses
      responses.forEach((response, index) => {
        console.log(`Processing response ${index}:`, response)
        console.log(`Response question:`, response.question)
        console.log(`Response keywords:`, response.keywords)
        console.log(`Response category:`, response.category)
        console.log(`Response language:`, response.language)
        console.log(`Response isActive:`, response.isActive)
        
        // Add the question as a suggested keyword
        if (response.question && !keywords.includes(response.question)) {
          keywords.push(response.question)
          console.log('Added question as keyword:', response.question)
        }
        
        // For pre-login users, also add individual keywords from the response
        // For post-login users, only show questions (skip keywords)
        if (!me && response.keywords && Array.isArray(response.keywords)) {
          response.keywords.forEach(keyword => {
            if (keyword && !keywords.includes(keyword)) {
              keywords.push(keyword)
              console.log('Added keyword:', keyword)
            }
          })
        }
      })
      
      console.log('Extracted keywords:', keywords)
      
      // Only use fallback if absolutely no keywords found from API
      if (keywords.length === 0) {
        console.log('No keywords from API, using fallback')
        
        // Smart fallback based on user status and context
        let fallbackKeywords: string[] = []
        
        if (me) {
          // Post-login fallback keywords - from chatbot management system
          fallbackKeywords = [
            "Help with my account", 
            "Technical support", 
            "Feature request", 
            "Report a bug"
          ]
        } else {
          // Pre-login fallback keywords - comprehensive format
          fallbackKeywords = getTranslatedQuestions()
        }
        
        keywords = fallbackKeywords
        console.log('Using fallback keywords:', keywords)
      } else {
        // Sort keywords by priority if available
        const sortedKeywords = keywords
          .sort((a, b) => {
            // Simple alphabetical sort for now
            return a.localeCompare(b)
          })
        
        keywords = sortedKeywords
        console.log('Using API keywords for pre-login user:', keywords)
        
        // For pre-login users, use comprehensive keywords
        if (!me) {
          // Use comprehensive keywords for pre-login users (no duplicates)
          keywords = getTranslatedQuestions()
          console.log('Using comprehensive keywords for pre-login user:', keywords)
        }
      }
      
      // Remove any duplicates
      keywords = [...new Set(keywords)]
      console.log('Final keywords to display (deduplicated):', keywords)
      
      // Safety check: ensure we always have keywords to display
      if (keywords.length === 0) {
        console.log('⚠️ No keywords found, setting emergency fallback')
        keywords = me
          ? ["Help with my account", "Technical support", "Feature request", "Report a bug"]
          : getTranslatedQuestions()
      }
      
      // Ensure no duplicates before setting
      const uniqueKeywords = [...new Set(keywords)]
      setSuggestedKeywords(uniqueKeywords)
      
      // If no keywords found, try a direct API call as a test
      if (keywords.length === 0) {
        console.log('=== TRYING DIRECT API CALL ===')
        try {
          const directUrl = `${process.env.REACT_APP_API_URL}/api/chatbot/responses?isActive=true&category=${category}&language=${intl.locale}&pageNumber=1&pageCount=10`
          console.log('Direct API URL:', directUrl)
          
          const directResponse = await fetch(directUrl)
          const directData = await directResponse.json()
          console.log('Direct API response:', directData)
          
          if (directData.data && directData.data.responses) {
            const directResponses = directData.data.responses
            console.log('Direct responses found:', directResponses.length)
            directResponses.forEach((resp: any, index: number) => {
              console.log(`Direct response ${index}:`, resp)
            })
          }
        } catch (directError) {
          console.error('Direct API call failed:', directError)
        }
      }
      
    } catch (error) {
      console.error('Error loading suggested keywords:', error)
      
      // Enhanced fallback keywords on error
      let fallbackKeywords: string[] = []
      
      if (me) {
        // Post-login fallback keywords
        fallbackKeywords = [
          "Help with my account", 
          "Technical support", 
          "Feature request", 
          "Report a bug"
        ]
        } else {
          // Pre-login fallback keywords - comprehensive format
          fallbackKeywords = [
            "Main partners", 
                        "What is Maarefati?",
            "How do I get started?",
            "What services do you offer?",
            "How can I create an account?",
            "How can I contact support?",
            "What languages are supported?",
            "Who can use Maarefati?",
            "Do you have mobile access?",
            "Why choose Maarefati?",
            "How can I join the Maarefati family?",
            "How do I register on Maarefati?",
            "How can I download the Maarefati app?"
          ]
        }
      
      console.log('Using fallback keywords on error:', fallbackKeywords)
      setSuggestedKeywords(fallbackKeywords)
    } finally {
      setLoadingSuggestions(false)
    }
  }

  // Cleanup effect to remove body class when component unmounts
  useEffect(() => {
    return () => {
      document.body.classList.remove('chatbot-open')
    }
  }, [])

  const loadActiveResponses = async () => {
    try {
      const category = me ? 'post-login' : 'pre-login'
      console.log('=== FLOATING CHATBOT LOADING RESPONSES ===')
      console.log('Loading chatbot responses for category:', category)
      console.log('User authenticated:', !!me)
      console.log('Language:', intl.locale)
      
      const result = await fetchActiveResponses({
        category,
        language: intl.locale as 'en' | 'fr' | 'ar',
        userRole: me?.role
      })
      
      console.log('=== FLOATING CHATBOT API RESULT ===')
      console.log('Fetch active responses result:', result)
      console.log('Result type:', typeof result)
      console.log('Result payload:', result.payload)
      console.log('Result payload type:', typeof result.payload)
      
      if (result.payload && typeof result.payload === 'object' && 'data' in result.payload) {
        const payload = result.payload as { data: { responses: ChatbotResponse[] } }
        console.log('=== FLOATING CHATBOT SUCCESS ===')
        console.log('Setting responses:', payload.data.responses)
        console.log('Responses length:', payload.data.responses.length)
        setResponses(payload.data.responses)
      } else {
        console.log('=== FLOATING CHATBOT NO RESPONSES ===')
        console.log('No responses found in payload:', result.payload)
        console.log('Payload keys:', result.payload ? Object.keys(result.payload) : 'No payload')
        
        // Fallback: try to get responses from the main chatbot state
        console.log('Trying fallback: checking main chatbot state...')
        // This is a temporary fallback - in a real app, you'd want to ensure the API works
        setResponses([])
      }
    } catch (error) {
      console.error('=== FLOATING CHATBOT ERROR ===')
      console.error('Error loading chatbot responses:', error)
      console.log('Setting empty responses due to error')
      setResponses([])
    }
  }

  const findBestResponse = (userQuestion: string): ChatbotResponse | null => {
    const question = userQuestion.toLowerCase().trim()
    
    // First, try exact keyword matches
    for (const response of responses) {
      for (const keyword of response.keywords) {
        if (question.includes(keyword.toLowerCase())) {
          return response
        }
      }
    }
    
    // Then try partial keyword matches
    for (const response of responses) {
      for (const keyword of response.keywords) {
        if (keyword.toLowerCase().includes(question) || question.includes(keyword.toLowerCase())) {
          return response
        }
      }
    }
    
    // Finally, try question similarity
    for (const response of responses) {
      if (response.question.toLowerCase().includes(question) || 
          question.includes(response.question.toLowerCase())) {
        return response
      }
    }
    
    return null
  }

  const handleSendMessage = async () => {
    if (!inputText.trim()) return

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: inputText,
      isUser: true,
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInputText('')
    setIsLoading(true)

    try {
      // First try to find a response from loaded responses
      let botResponse = findBestResponse(inputText)
      
      // If no response found, try searching the API
      if (!botResponse) {
        const searchResult = await searchResponses({
          query: inputText,
          category: me ? 'post-login' : 'pre-login',
          language: intl.locale as 'en' | 'fr' | 'ar',
          userRole: me?.role
        })
        
        if (searchResult.payload && typeof searchResult.payload === 'object' && 'bestMatch' in searchResult.payload) {
          const payload = searchResult.payload as { bestMatch: ChatbotResponse }
          if (payload.bestMatch) {
            botResponse = {
              _id: 'search-result',
              question: payload.bestMatch.question,
              answer: payload.bestMatch.answer,
              keywords: [],
              category: me ? 'post-login' : 'pre-login',
              language: intl.locale as 'en' | 'fr' | 'ar',
              isActive: true,
              priority: 1
            }
          }
        }
      }

      const botMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: botResponse?.answer || intl.formatMessage({id: 'CHATBOT.NO_ANSWER_FOUND'}),
        isUser: false,
        timestamp: new Date()
      }

      setMessages(prev => [...prev, botMessage])
      
      // Show notification if chatbot is closed
      if (!isOpen) {
        setHasNewMessage(true)
        setUnreadCount(prev => prev + 1)
      }
    } catch (error) {
      console.error('Error getting chatbot response:', error)
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: intl.formatMessage({id: 'CHATBOT.ERROR_OCCURRED'}),
        isUser: false,
        timestamp: new Date()
      }
      setMessages(prev => [...prev, errorMessage])
      
      // Show notification for error messages too
      if (!isOpen) {
        setHasNewMessage(true)
        setUnreadCount(prev => prev + 1)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleSuggestionClick = (suggestion: string) => {
    setInputText(suggestion)
    // Keep suggestions visible for better UX
    // setShowSuggestions(false)
    // Auto-send the suggestion
    setTimeout(() => {
      handleSendMessage()
    }, 100)
  }

  const handleInputFocus = () => {
    // Always show suggestions when input is focused
    setShowSuggestions(true)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value)
    // Keep suggestions visible for better UX - don't hide when typing
    // if (e.target.value.trim()) {
    //   setShowSuggestions(false)
    // } else if (responses.length > 0) {
    //   setShowSuggestions(true)
    // }
  }

  const getSuggestedQuestions = (): string[] => {
    console.log('Getting suggested questions from responses:', responses)
    const suggestions: string[] = []
    responses.forEach(response => {
      if (response.question && !suggestions.includes(response.question)) {
        suggestions.push(response.question)
      }
      response.keywords.forEach(keyword => {
        if (!suggestions.includes(keyword)) {
          suggestions.push(keyword)
        }
      })
    })
    console.log('Generated suggestions:', suggestions)
    return suggestions // Show all suggestions
  }

  const toggleChatbot = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    const newIsOpen = !isOpen
    setIsOpen(newIsOpen)
    
    // Prevent body scroll when modal is open
    if (newIsOpen) {
      document.body.classList.add('chatbot-open')
      // Reset notification states when opening
      setHasNewMessage(false)
      setUnreadCount(0)
      
      if (messages.length === 0) {
        // Add welcome message when opening for the first time
        const welcomeMessage: ChatMessage = {
          id: 'welcome',
          text: isPreLogin 
            ? `Good ${new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, ${me?.firstName || 'there'}! Can I help you with anything?`
            : `Hello ${me?.firstName || 'there'}! How can I assist you today?`,
          isUser: false,
          timestamp: new Date()
        }
        setMessages([welcomeMessage])
        setShowSuggestions(true)
        
        // Ensure suggestions are always visible
        console.log('Chatbot opened, ensuring suggestions are visible')
        
        // Ensure we have keywords to show immediately
    if (suggestedKeywords.length === 0) {
      const fallbackKeywords = me
        ? ["Help with my account", "Technical support", "Feature request", "Report a bug"]
        : getTranslatedQuestions()
      console.log('Setting fallback keywords in toggle:', fallbackKeywords)
      setSuggestedKeywords(fallbackKeywords)
    }
      }
    } else {
      document.body.classList.remove('chatbot-open')
    }
  }

  return (
    <>
      {/* Floating Chatbot Button */}
      <div className="floating-chatbot-button-container">
        <button
          className="floating-chatbot-button"
          onClick={toggleChatbot}
          title={intl.formatMessage({id: 'CHATBOT.OPEN_CHAT'})}
        >
          {/* Custom Animated Sparkle Icon */}
          <div className="sparkle-icon">
            <div className="sparkle-main">✦</div>
            <div className="sparkle-small sparkle-1">✧</div>
            <div className="sparkle-small sparkle-2">✧</div>
          </div>
          {hasNewMessage && unreadCount > 0 && (
            <div className="floating-chatbot-notification">
              {unreadCount > 9 ? '9+' : unreadCount}
            </div>
          )}
          {/* AI Particle Effect */}
          <div className="ai-particles">
            <div className="ai-particle"></div>
            <div className="ai-particle"></div>
            <div className="ai-particle"></div>
          </div>
        </button>
      </div>

      {/* Chatbot Modal */}
      {isOpen && (
        <div className="floating-chatbot-modal">
          {/* Header */}
          <div className="floating-chatbot-header">
            <div className="d-flex align-items-center">
              <div className="chatbot-avatar">
                <div className="sparkle-icon">
                  <div className="sparkle-main">✦</div>
                  <div className="sparkle-small sparkle-1">✧</div>
                  <div className="sparkle-small sparkle-2">✧</div>
                </div>
              </div>
              <div className="chatbot-title">
                <span className="fw-bold">MaarefatiAI</span>
                <small className="d-block text-white-50">AI-Powered Support</small>
              </div>
            </div>
            <button
              className="btn btn-sm btn-light"
              onClick={() => {
                setIsOpen(false)
                document.body.classList.remove('chatbot-open')
              }}
              title={intl.formatMessage({id: 'CHATBOT.CLOSE'})}
            >
              <KTSVG path="/media/icons/duotune/arrows/arr061.svg" className="svg-icon-1" />
            </button>
          </div>

          {/* Messages */}
          <div className="floating-chatbot-messages">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`floating-chatbot-message ${message.isUser ? 'user' : 'bot'}`}
              >
                <div className="floating-chatbot-message-bubble">
                  {message.text}
                </div>
                <div className="floating-chatbot-message-time">
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="floating-chatbot-loading">
                <div className="floating-chatbot-typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}

            {/* Suggested Keywords in the middle of messages */}
            {showSuggestions && (
              <div className="floating-chatbot-suggestions-middle">
                <div className="floating-chatbot-suggestions-header">
                  <span className="fw-bold">
                    {isPreLogin 
                      ? "Choose a prompt below or write your own to start chatting with MaarefatiAI"
                      : "Quick actions to get started"
                    }
                  </span>
                </div>
                
                {loadingSuggestions ? (
                  <div className="floating-chatbot-suggestions-loading">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading suggestions...</span>
                    </div>
                    <span className="ms-2">Loading suggestions...</span>
                  </div>
                ) : (
                  <>
                    <div className="floating-chatbot-suggestions-list">
                      {suggestedKeywords.length > 0 ? (
                        <div className="floating-chatbot-suggestions-scroll">
                          {suggestedKeywords.map((keyword, index) => (
                            <button
                              key={index}
                              className="floating-chatbot-suggestion-item"
                              onClick={() => handleSuggestionClick(keyword)}
                            >
                              {keyword}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="floating-chatbot-no-suggestions">
                          <p className="text-muted">No suggestions available. Try typing your question below.</p>
                        </div>
                      )}
                    </div>
                    <div className="floating-chatbot-refresh-prompts">
                      <button 
                        className="btn btn-link btn-sm p-0"
                        onClick={async () => {
                          console.log('Manual refresh triggered')
                          await loadSuggestedKeywords()
                          // Show a brief loading state
                          setShowSuggestions(false)
                          setTimeout(() => setShowSuggestions(true), 100)
                        }}
                        disabled={loadingSuggestions}
                      >
                        {loadingSuggestions ? 'Loading...' : 'Refresh prompts'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* Only show fallback if API completely fails and no keywords are loaded */}
            {showSuggestions && suggestedKeywords.length === 0 && !loadingSuggestions && (
              <div className="floating-chatbot-suggestions-middle">
                <div className="floating-chatbot-suggestions-header">
                  <span className="fw-bold">
                    {isPreLogin 
                      ? "Choose a prompt below or write your own to start chatting with MaarefatiAI"
                      : "Quick actions to get started"
                    }
                  </span>
                </div>
                <div className="floating-chatbot-suggestions-list">
                  <div className="floating-chatbot-no-suggestions">
                    <p className="text-muted">Loading suggestions from database...</p>
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={async () => {
                        await loadSuggestedKeywords()
                      }}
                      disabled={loadingSuggestions}
                    >
                      {loadingSuggestions ? 'Loading...' : 'Load Suggestions'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="floating-chatbot-input">
            <div className="floating-chatbot-input-group">
              <input
                type="text"
                placeholder={isPreLogin 
                  ? "How can MaarefatiAI help you today?" 
                  : "Reply to MaarefatiAI"
                }
                value={inputText}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
              />
              <div className="floating-chatbot-input-actions">
                <button className="floating-chatbot-action-btn" title="Camera">
                  <KTSVG path="/media/icons/duotune/general/gen049.svg" className="svg-icon-1" />
                </button>
                <button className="floating-chatbot-action-btn" title="Attach">
                  <KTSVG path="/media/icons/duotune/general/gen004.svg" className="svg-icon-1" />
                </button>
                <button
                  className="floating-chatbot-send-button"
                  onClick={handleSendMessage}
                  disabled={!inputText.trim() || isLoading}
                  title={intl.formatMessage({id: 'CHATBOT.SEND'})}
                >
                  <KTSVG path="/media/icons/duotune/arrows/arr064.svg" className="svg-icon-1" />
                </button>
              </div>
            </div>
            <div className="floating-chatbot-disclaimer">
              <small className="text-muted">Please double-check responses.</small>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
